/**
 * Vuetify 3.5 Plugin Configuration
 *
 * @version 2.0.0
 * @description Vuetify 3.5 setup with theme layers, CSS variables, and component defaults
 * @see https://vuetifyjs.com/en/features/theme/
 *
 * ARCHITECTURE:
 * - This file is the SINGLE SOURCE OF TRUTH for all colors
 * - Theme composable (useTheme) reads from here via Vuetify API
 * - SCSS variables.scss only contains layout variables (no colors)
 * - Components use CSS custom properties: rgb(var(--v-theme-primary))
 */

import { createVuetify, type ThemeDefinition } from 'vuetify'
import { md3 } from 'vuetify/blueprints'

// Icon System — Material Symbols via @iconify/vue
import { ms, msAliases } from './iconsets/materialSymbols'

// Component imports for Aliases (Vuetify 3.4+)
import { VTextField } from 'vuetify/components/VTextField'
import { VSelect } from 'vuetify/components/VSelect'
import { VBtn } from 'vuetify/components/VBtn'
import { VAutocomplete } from 'vuetify/components/VAutocomplete'
import { VTextarea } from 'vuetify/components/VTextarea'

import dayjs from 'dayjs'
import DayJsAdapter from '@date-io/dayjs'
import it from 'dayjs/locale/it'
// Messaggi UI Vuetify in italiano (aria-label, paginazione, "nessun dato", ecc.)
import { it as vuetifyIt } from 'vuetify/locale'

// Apply Italian locale to dayjs globally — picked up by DayJsAdapter automatically
dayjs.locale(it)

// =============================================================================
// Color Palette - Gandolfi Group Brand
// =============================================================================

/**
 * Brand colors - exported for use in non-Vuetify contexts (charts, etc.)
 */
export const BRAND_COLORS = {
  // Primary palette (Gandolfi Blue)
  primary: '#C2050D',
  primaryDark: '#232F34',
  primaryLight: '#4A6572',

  // Secondary palette (Gandolfi Gold)
  secondary: '#6F6F6E',
  secondaryDark: '#E09A2D',
  secondaryLight: '#FFBB55',

  // Accent (alias for secondary)
  accent: '#E09A2D',

  // Sub-brand colors
  ctf: '#C2050D',      // CTF brand
  mkt: '#1976D2',      // Marketing brand
  srv: '#f37021',      // Service brand
  sys: '#1976D2',      // System brand

  // Semantic colors
  success: '#4CAF50',
  successDark: '#388E3C',
  info: '#2196F3',
  infoDark: '#1976D2',
  warning: '#FB8C00',
  warningDark: '#F57C00',
  error: '#FF5252',
  errorDark: '#D32F2F',

  // Neutral colors
  white: '#FFFFFF',
  black: '#000000',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
} as const

/**
 * Company labels — human-readable names for each company code.
 * Used in mobile cards, chips, badges across the app.
 */
export const COMPANY_LABELS: Record<string, string> = {
  ctf: 'CTF',
  mkt: 'Gandolfi Group',
  srv: 'Service',
  sys: 'System',
} as const

// =============================================================================
// Theme Definition - Light
// =============================================================================

const gandolfiLightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    // Primary palette
    primary: BRAND_COLORS.primary,
    'primary-darken-1': BRAND_COLORS.primaryDark,
    'primary-lighten-1': BRAND_COLORS.primaryLight,

    // Secondary palette (Accent)
    secondary: BRAND_COLORS.secondary,
    'secondary-darken-1': BRAND_COLORS.secondaryDark,
    'secondary-lighten-1': BRAND_COLORS.secondaryLight,
    accent: BRAND_COLORS.accent,

    // Semantic colors
    error: BRAND_COLORS.error,
    'error-darken-1': BRAND_COLORS.errorDark,
    info: BRAND_COLORS.info,
    'info-darken-1': BRAND_COLORS.infoDark,
    success: BRAND_COLORS.success,
    'success-darken-1': BRAND_COLORS.successDark,
    warning: BRAND_COLORS.warning,
    'warning-darken-1': BRAND_COLORS.warningDark,

    // Background & Surface
    background: BRAND_COLORS.white,
    surface: BRAND_COLORS.white,
    'surface-bright': BRAND_COLORS.white,
    'surface-light': BRAND_COLORS.grey[50],
    'surface-variant': BRAND_COLORS.grey[200],

    // Text colors (on-* for contrast)
    'on-background': BRAND_COLORS.primaryDark,
    'on-surface': BRAND_COLORS.primaryDark,
    'on-surface-variant': BRAND_COLORS.grey[800],
    'on-primary': BRAND_COLORS.white,
    'on-secondary': BRAND_COLORS.primaryDark,
    'on-error': BRAND_COLORS.white,
    'on-info': BRAND_COLORS.white,
    'on-success': BRAND_COLORS.white,
    'on-warning': BRAND_COLORS.primaryDark,

    // Custom brand colors (accessible via rgb(var(--v-theme-ctf-brand)))
    'ctf-brand': BRAND_COLORS.ctf,
    'mkt-brand': BRAND_COLORS.mkt,
    'srv-brand': BRAND_COLORS.srv,
    'sys-brand': BRAND_COLORS.sys,

    // UI specific colors
    'toolbar-bg': BRAND_COLORS.white,
    'drawer-bg': BRAND_COLORS.white,
    'table-header': BRAND_COLORS.grey[100],
    'table-border': BRAND_COLORS.grey[300],
    'table-hover': BRAND_COLORS.grey[50],
    'input-bg': BRAND_COLORS.grey[100],
  },
  variables: {
    // Border
    'border-color': BRAND_COLORS.grey[300],
    'border-opacity': 0.12,

    // Text emphasis
    'high-emphasis-opacity': 0.87,
    'medium-emphasis-opacity': 0.60,
    'disabled-opacity': 0.38,

    // Interactive states
    'idle-opacity': 0.04,
    'hover-opacity': 0.08,
    'focus-opacity': 0.12,
    'selected-opacity': 0.08,
    'activated-opacity': 0.12,
    'pressed-opacity': 0.16,
    'dragged-opacity': 0.08,

    // Code blocks
    'theme-kbd': '#212529',
    'theme-on-kbd': BRAND_COLORS.white,
    'theme-code': BRAND_COLORS.grey[100],
    'theme-on-code': BRAND_COLORS.black,
  }
}

// =============================================================================
// Theme Definition - Dark
// =============================================================================

const gandolfiDarkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    // Primary palette (lightened for dark mode)
    primary: BRAND_COLORS.primaryLight,
    'primary-darken-1': BRAND_COLORS.primary,
    'primary-lighten-1': '#607D8B',

    // Secondary palette
    secondary: BRAND_COLORS.secondary,
    'secondary-darken-1': BRAND_COLORS.secondaryDark,
    'secondary-lighten-1': BRAND_COLORS.secondaryLight,
    accent: BRAND_COLORS.accent,

    // Semantic colors
    error: BRAND_COLORS.error,
    info: BRAND_COLORS.info,
    success: BRAND_COLORS.success,
    warning: BRAND_COLORS.warning,

    // Background & Surface
    background: '#121212',
    surface: '#1E1E1E',
    'surface-bright': '#2D2D2D',
    'surface-light': '#383838',
    'surface-variant': BRAND_COLORS.grey[800],

    // Text colors
    'on-background': BRAND_COLORS.white,
    'on-surface': BRAND_COLORS.white,
    'on-surface-variant': BRAND_COLORS.grey[300],
    'on-primary': BRAND_COLORS.white,
    'on-secondary': BRAND_COLORS.primaryDark,

    // Custom brand colors (lighter for dark mode)
    'ctf-brand': BRAND_COLORS.primaryLight,
    'mkt-brand': '#42A5F5',
    'srv-brand': '#FF8A65',
    'sys-brand': '#BA68C8',

    // UI specific colors
    'header-bg': '#2D2D2D',
    'toolbar-bg': '#1E1E1E',
    'drawer-bg': '#1E1E1E',
    'table-header': '#2D2D2D',
    'table-border': BRAND_COLORS.grey[800],
    'table-hover': '#2D2D2D',
    'input-bg': '#2D2D2D',
  },
  variables: {
    'border-color': BRAND_COLORS.grey[800],
    'border-opacity': 0.12,
    'high-emphasis-opacity': 1,
    'medium-emphasis-opacity': 0.70,
    'disabled-opacity': 0.50,
    'idle-opacity': 0.10,
    'hover-opacity': 0.15,
    'focus-opacity': 0.15,
    'selected-opacity': 0.15,
    'activated-opacity': 0.20,
    'pressed-opacity': 0.20,
    'dragged-opacity': 0.15,
  }
}

// =============================================================================
// Theme Layers - For Section-Specific Theming
// =============================================================================

/**
 * Marketing section theme layer
 * Apply with: <v-theme-provider theme="marketingLayer">
 */
const marketingLayerTheme: ThemeDefinition = {
  dark: false,
  colors: {
    primary: BRAND_COLORS.mkt,
    'primary-darken-1': '#1565C0',
    'primary-lighten-1': '#42A5F5',
    'header-bg': '#E3F2FD',
  }
}

/**
 * Service section theme layer
 * Apply with: <v-theme-provider theme="serviceLayer">
 */
const serviceLayerTheme: ThemeDefinition = {
  dark: false,
  colors: {
    primary: BRAND_COLORS.srv,
    'primary-darken-1': '#E64A19',
    'primary-lighten-1': '#FF8A65',
    'header-bg': '#FBE9E7',
  }
}

/**
 * Tech section theme layer
 * Apply with: <v-theme-provider theme="techLayer">
 */
const techLayerTheme: ThemeDefinition = {
  dark: false,
  colors: {
    primary: BRAND_COLORS.ctf,
    'primary-darken-1': BRAND_COLORS.primaryDark,
    'primary-lighten-1': BRAND_COLORS.primaryLight,
    'header-bg': '#E8EAF6',
  }
}

// =============================================================================
// Component Defaults
// =============================================================================

const defaults = {
  // Global settings
  global: {
    ripple: true,
  },

  // ==========================================================================
  // Layout Components
  // ==========================================================================

  VApp: {
    // No defaults needed
  },

  VAppBar: {
    elevation: 0,
    density: 'default',
  },

  VNavigationDrawer: {
    elevation: 0,
  },

  VMain: {
    // No defaults needed
  },

  VFooter: {
    elevation: 0,
  },

  // ==========================================================================
  // Form Inputs
  // ==========================================================================

  VTextField: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },

  VTextarea: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },

  VSelect: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },

  VAutocomplete: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },

  VCombobox: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },

  VFileInput: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },

  VSwitch: {
    color: 'primary',
    inset: true,
    hideDetails: 'auto',
  },

  VCheckbox: {
    color: 'primary',
    hideDetails: 'auto',
  },

  VRadio: {
    color: 'primary',
  },

  VRadioGroup: {
    color: 'primary',
    hideDetails: 'auto',
  },

  VSlider: {
    color: 'primary',
    hideDetails: 'auto',
  },

  VRangeSlider: {
    color: 'primary',
    hideDetails: 'auto',
  },

  // ==========================================================================
  // Buttons
  // ==========================================================================

  VBtn: {
    variant: 'flat',
    style: 'text-transform: none; font-weight: 500;',
  },

  VBtnToggle: {
    color: 'primary',
    variant: 'outlined',
  },

  // ==========================================================================
  // Cards & Containers
  // ==========================================================================

  VCard: {
    elevation: 0,
    border: true,
  },

  VSheet: {
    elevation: 0,
  },

  VExpansionPanels: {
    variant: 'accordion',
  },

  VExpansionPanel: {
    elevation: 0,
  },

  // ==========================================================================
  // Data Display
  // ==========================================================================

  VDataTable: {
    fixedHeader: true,
    hover: true,
  },

  VDataTableServer: {
    fixedHeader: true,
    hover: true,
  },

  VTable: {
    hover: true,
  },

  // ==========================================================================
  // Lists
  // ==========================================================================

  VList: {
    lines: 'one',
    density: 'default',
  },

  VListItem: {
    minHeight: 48,
  },

  VListSubheader: {
    color: 'primary',
  },

  // ==========================================================================
  // Navigation
  // ==========================================================================

  VTabs: {
    color: 'primary',
  },

  VTab: {
    // No defaults needed
  },

  VPagination: {
    activeColor: 'primary',
    variant: 'flat',
    rounded: true,
  },

  VBreadcrumbs: {
    divider: '/',
  },

  // ==========================================================================
  // Feedback & Overlays
  // ==========================================================================

  VDialog: {
    scrim: 'rgba(0, 0, 0, 0.5)',
    scrollable: true,
  },

  VMenu: {
    transition: 'slide-y-transition',
  },

  VTooltip: {
    transition: 'fade-transition',
  },

  VSnackbar: {
    timeout: 3000,
    location: 'bottom',
  },

  VAlert: {
    variant: 'tonal',
    border: 'start',
  },

  VBanner: {
    border: 'bottom',
  },

  VOverlay: {
    scrim: 'rgba(0, 0, 0, 0.5)',
  },

  // ==========================================================================
  // Progress Indicators
  // ==========================================================================

  VProgressCircular: {
    color: 'primary',
  },

  VProgressLinear: {
    color: 'primary',
  },

  VSkeleton: {
    boilerplate: false,
  },

  // ==========================================================================
  // Chips & Badges
  // ==========================================================================

  VChip: {
    variant: 'flat',
    size: 'default',
  },

  VBadge: {
    color: 'error',
  },

  // ==========================================================================
  // Icons & Images
  // ==========================================================================

  VIcon: {
    // Default size handled by context
  },

  VAvatar: {
    size: 40,
  },

  VImg: {
    transition: 'fade-transition',
  },

  // ==========================================================================
  // Date/Time (Labs - experimental)
  // ==========================================================================

  VDatePicker: {
    color: 'primary',
  },

  VTimePicker: {
    color: 'primary',
  },

  // ==========================================================================
  // Gg Aliases — Custom Presets
  // These defaults apply ONLY to Gg* components, not to base V* components.
  // ==========================================================================

  GgTextField: {
    variant: 'outlined',
    density: 'compact',
    bgColor: 'surface-container-lowest',
    hideDetails: 'auto',
    clearable: true,
    singleLine: true,
    class: 'gg-text-field',
  },

  GgSelect: {
    variant: 'outlined',
    density: 'compact',
    bgColor: 'surface-container-lowest',
    hideDetails: 'auto',
    class: 'gg-select',
    itemTitle: "display",
    itemValue: "value",
    clearable: true
  },

  GgAutocomplete: {
    variant: 'outlined',
    density: 'compact',
    bgColor: 'surface-container-lowest',
    hideDetails: 'auto',
    hideNoData: true,
    class: 'gg-autocomplete',
    itemTitle: "display",
    itemValue: "value",
    clearable: true
  },

  GgTextarea: {
    variant: 'outlined',
    density: 'compact',
    bgColor: 'surface-container-lowest',
    hideDetails: 'auto',
    clearable: true,
    class: 'gg-textarea',
  },

  GgBtn: {
    variant: 'flat',
    rounded: 'lg',
    class: 'gg-btn',
  },

  GgBtnIcon: {
    variant: 'text',
    density: 'compact',
    size: 'large',
    icon: true,
    class: 'gg-btn-icon',
  },

}

// =============================================================================
// Create Vuetify Instance
// =============================================================================

// =============================================================================
// Theme Persistence — Initial Theme Resolution
// =============================================================================
// Priority: localStorage (user explicit choice) → OS preference → 'light'
// We only read here; writing is handled by toggleTheme() in theme-mixin.ts

export const THEME_STORAGE_KEY = 'gandolfi-theme-preference'

function getInitialTheme(): 'light' | 'dark' {
  // 1. Check localStorage for explicit user preference
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') return saved
  } catch {
    // localStorage not available (SSR, privacy mode) — fall through
  }
  // 2. Detect OS preference
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  // 3. Default
  return 'light'
}

const vuetify = createVuetify({
  // ===========================================================================
  // Aliases — Custom preset components (Vuetify 3.4+)
  // Usage: <GgTextField /> <GgSelect /> <GgAutocomplete /> <GgBtn color="primary" />
  //         <GgBtnIcon icon="edit" /> (compact icon-only button)
  // These inherit ALL props/events/slots/refs from their base component.
  // Per-instance overrides still work: <GgTextField variant="plain" />
  // ===========================================================================
  aliases: {
    GgTextField: VTextField,
    GgSelect: VSelect,
    GgAutocomplete: VAutocomplete,
    GgBtn: VBtn,
    GgBtnIcon: VBtn,
    GgTextarea: VTextarea,
  },

  theme: {
    defaultTheme: getInitialTheme(),
    themes: {
      // Base themes
      gandolfiLight: gandolfiLightTheme,
      gandolfiDark: gandolfiDarkTheme,
      // Theme layers (for v-theme-provider)
      marketingLayer: marketingLayerTheme,
      serviceLayer: serviceLayerTheme,
      techLayer: techLayerTheme,
      light: {
        colors: {
          background: '#eeeeee',
          surface: '#f9f9f9',
          'surface-dim': '#dadada',
          'surface-bright': '#f9f9f9',
          'surface-container-lowest': '#ffffff',
          'surface-container-low': '#f3f3f3',
          'surface-container': '#eeeeee',
          'surface-container-high': '#e8e8e8',
          'surface-container-highest': '#e2e2e2',
          'on-surface': '#1a1c1c',
          'inverse-surface': '#2f3131',
          'inverse-on-surface': '#f1f1f1',
          outline: '#767777',
          'outline-variant': '#c6c6c6',
          primary: '#c0020b',
          'on-primary': '#ffffff',
          'primary-container': '#ffdad5',
          'on-primary-container': '#410001',
          'inverse-primary': '#ffb4aa',
          secondary: '#00658b',
          'on-secondary': '#ffffff',
          'secondary-container': '#c4e7ff',
          'on-secondary-container': '#001e2c',
          tertiary: '#006b57',
          'on-tertiary': '#ffffff',
          'tertiary-container': '#9df3d9',
          'on-tertiary-container': '#002019',
          error: '#ba1a1a',
          'on-error': '#ffffff',
          'error-container': '#ffdad6',
          'on-error-container': '#410002',
          'primary-fixed': '#ffdad5',
          'primary-fixed-dim': '#ffb4aa',
          'on-primary-fixed': '#410001',
          'on-primary-fixed-variant': '#930006',
          'secondary-fixed': '#c4e7ff',
          'secondary-fixed-dim': '#86cffa',
          'on-secondary-fixed': '#001e2c',
          'on-secondary-fixed-variant': '#004c69',
          'tertiary-fixed': '#9df3d9',
          'tertiary-fixed-dim': '#81d6bd',
          'on-tertiary-fixed': '#002019',
          'on-tertiary-fixed-variant': '#005141',
          'surface-light': '#e8e8e8',
        },
        dark: false,
        variables: {
          'overlay-background': '#1a1c1c',
        },
      },
      'light-medium-contrast': {
        colors: {
          background: '#e8e8e8',
          surface: '#f9f9f9',
          'surface-dim': '#c6c6c7',
          'surface-bright': '#f9f9f9',
          'surface-container-lowest': '#ffffff',
          'surface-container-low': '#f3f3f3',
          'surface-container': '#e8e8e8',
          'surface-container-high': '#dcdddd',
          'surface-container-highest': '#d1d2d2',
          'on-surface': '#0f1112',
          'inverse-surface': '#2f3131',
          'inverse-on-surface': '#f1f1f1',
          outline: '#515353',
          'outline-variant': '#6c6d6d',
          primary: '#740003',
          'on-primary': '#ffffff',
          'primary-container': '#d61d1a',
          'on-primary-container': '#ffffff',
          'inverse-primary': '#ffb4aa',
          secondary: '#003b52',
          'on-secondary': '#ffffff',
          'secondary-container': '#1f759b',
          'on-secondary-container': '#ffffff',
          tertiary: '#003e31',
          'on-tertiary': '#ffffff',
          'tertiary-container': '#1c7b65',
          'on-tertiary-container': '#ffffff',
          error: '#740006',
          'on-error': '#ffffff',
          'error-container': '#cf2c27',
          'on-error-container': '#ffffff',
          'primary-fixed': '#d61d1a',
          'primary-fixed-dim': '#ae0008',
          'on-primary-fixed': '#ffffff',
          'on-primary-fixed-variant': '#ffffff',
          'secondary-fixed': '#1f759b',
          'secondary-fixed-dim': '#005b7d',
          'on-secondary-fixed': '#ffffff',
          'on-secondary-fixed-variant': '#ffffff',
          'tertiary-fixed': '#1c7b65',
          'tertiary-fixed-dim': '#00604e',
          'on-tertiary-fixed': '#ffffff',
          'on-tertiary-fixed-variant': '#ffffff',
          'surface-light': '#e8e8e8',
        },
        dark: false,
        variables: {
          'overlay-background': '#1a1c1c',
        },
      },
      'light-high-contrast': {
        colors: {
          background: '#e2e2e2',
          surface: '#f9f9f9',
          'surface-dim': '#b8b9b9',
          'surface-bright': '#f9f9f9',
          'surface-container-lowest': '#ffffff',
          'surface-container-low': '#f1f1f1',
          'surface-container': '#e2e2e2',
          'surface-container-high': '#d4d4d4',
          'surface-container-highest': '#c6c6c7',
          'on-surface': '#000000',
          'inverse-surface': '#2f3131',
          'inverse-on-surface': '#ffffff',
          outline: '#2b2c2d',
          'outline-variant': '#48494a',
          primary: '#610002',
          'on-primary': '#ffffff',
          'primary-container': '#980006',
          'on-primary-container': '#ffffff',
          'inverse-primary': '#ffb4aa',
          secondary: '#003044',
          'on-secondary': '#ffffff',
          'secondary-container': '#004f6d',
          'on-secondary-container': '#ffffff',
          tertiary: '#003328',
          'on-tertiary': '#ffffff',
          'tertiary-container': '#005343',
          'on-tertiary-container': '#ffffff',
          error: '#600004',
          'on-error': '#ffffff',
          'error-container': '#98000a',
          'on-error-container': '#ffffff',
          'primary-fixed': '#980006',
          'primary-fixed-dim': '#6d0003',
          'on-primary-fixed': '#ffffff',
          'on-primary-fixed-variant': '#ffffff',
          'secondary-fixed': '#004f6d',
          'secondary-fixed-dim': '#00374d',
          'on-secondary-fixed': '#ffffff',
          'on-secondary-fixed-variant': '#ffffff',
          'tertiary-fixed': '#005343',
          'tertiary-fixed-dim': '#003a2e',
          'on-tertiary-fixed': '#ffffff',
          'on-tertiary-fixed-variant': '#ffffff',
          'surface-light': '#e8e8e8',
        },
        dark: false,
        variables: {
          'overlay-background': '#1a1c1c',
        },
      },
      dark: {
        colors: {
          background: '#1e2020',
          surface: '#121414',
          'surface-dim': '#121414',
          'surface-bright': '#38393a',
          'surface-container-lowest': '#0c0f0f',
          'surface-container-low': '#1a1c1c',
          'surface-container': '#1e2020',
          'surface-container-high': '#282a2b',
          'surface-container-highest': '#333535',
          'on-surface': '#e2e2e2',
          'inverse-surface': '#e2e2e2',
          'inverse-on-surface': '#2f3131',
          outline: '#909191',
          'outline-variant': '#454747',
          primary: '#ffb4aa',
          'on-primary': '#690003',
          'primary-container': '#930006',
          'on-primary-container': '#ffdad5',
          'inverse-primary': '#c0020b',
          secondary: '#86cffa',
          'on-secondary': '#00344a',
          'secondary-container': '#004c69',
          'on-secondary-container': '#c4e7ff',
          tertiary: '#81d6bd',
          'on-tertiary': '#00382c',
          'tertiary-container': '#005141',
          'on-tertiary-container': '#9df3d9',
          error: '#ffb4ab',
          'on-error': '#690005',
          'error-container': '#93000a',
          'on-error-container': '#ffb4ab',
          'primary-fixed': '#ffdad5',
          'primary-fixed-dim': '#ffb4aa',
          'on-primary-fixed': '#410001',
          'on-primary-fixed-variant': '#930006',
          'secondary-fixed': '#c4e7ff',
          'secondary-fixed-dim': '#86cffa',
          'on-secondary-fixed': '#001e2c',
          'on-secondary-fixed-variant': '#004c69',
          'tertiary-fixed': '#9df3d9',
          'tertiary-fixed-dim': '#81d6bd',
          'on-tertiary-fixed': '#002019',
          'on-tertiary-fixed-variant': '#005141',
          'surface-light': '#38393a',
        },
        dark: true,
        variables: {
          'overlay-background': '#1a1c1c',
        },
      },
      'dark-medium-contrast': {
        colors: {
          background: '#262829',
          surface: '#121414',
          'surface-dim': '#121414',
          'surface-bright': '#434545',
          'surface-container-lowest': '#060808',
          'surface-container-low': '#1c1e1e',
          'surface-container': '#262829',
          'surface-container-high': '#313333',
          'surface-container-highest': '#3c3e3e',
          'on-surface': '#ffffff',
          'inverse-surface': '#e2e2e2',
          'inverse-on-surface': '#282a2b',
          outline: '#b1b2b2',
          'outline-variant': '#8f9090',
          primary: '#ffd2cc',
          'on-primary': '#540002',
          'primary-container': '#740003',
          'on-primary-container': '#ffdad5',
          'inverse-primary': '#950006',
          secondary: '#b6e2ff',
          'on-secondary': '#00293b',
          'secondary-container': '#003b52',
          'on-secondary-container': '#c4e7ff',
          tertiary: '#97edd2',
          'on-tertiary': '#002c22',
          'tertiary-container': '#003e31',
          'on-tertiary-container': '#9df3d9',
          error: '#ffd2cc',
          'on-error': '#540003',
          'error-container': '#740006',
          'on-error-container': '#ffd2cc',
          'primary-fixed': '#ffdad5',
          'primary-fixed-dim': '#ffb4aa',
          'on-primary-fixed': '#2d0000',
          'on-primary-fixed-variant': '#740003',
          'secondary-fixed': '#c4e7ff',
          'secondary-fixed-dim': '#86cffa',
          'on-secondary-fixed': '#00131e',
          'on-secondary-fixed-variant': '#003b52',
          'tertiary-fixed': '#9df3d9',
          'tertiary-fixed-dim': '#81d6bd',
          'on-tertiary-fixed': '#00150f',
          'on-tertiary-fixed-variant': '#003e31',
          'surface-light': '#38393a',
        },
        dark: true,
        variables: {
          'overlay-background': '#1a1c1c',
        },
      },
      'dark-high-contrast': {
        colors: {
          background: '#2f3131',
          surface: '#121414',
          'surface-dim': '#121414',
          'surface-bright': '#4f5051',
          'surface-container-lowest': '#000000',
          'surface-container-low': '#1e2020',
          'surface-container': '#2f3131',
          'surface-container-high': '#3a3c3c',
          'surface-container-highest': '#454747',
          'on-surface': '#ffffff',
          'inverse-surface': '#e2e2e2',
          'inverse-on-surface': '#000000',
          outline: '#f0f0f0',
          'outline-variant': '#c2c3c3',
          primary: '#ffece9',
          'on-primary': '#000000',
          'primary-container': '#2d0000',
          'on-primary-container': '#ffdad5',
          'inverse-primary': '#950006',
          secondary: '#e2f2ff',
          'on-secondary': '#000000',
          'secondary-container': '#00131e',
          'on-secondary-container': '#c4e7ff',
          tertiary: '#b4ffe7',
          'on-tertiary': '#000000',
          'tertiary-container': '#00150f',
          'on-tertiary-container': '#9df3d9',
          error: '#ffece9',
          'on-error': '#000000',
          'error-container': '#2d0001',
          'on-error-container': '#ffece9',
          'primary-fixed': '#ffdad5',
          'primary-fixed-dim': '#ffb4aa',
          'on-primary-fixed': '#000000',
          'on-primary-fixed-variant': '#2d0000',
          'secondary-fixed': '#c4e7ff',
          'secondary-fixed-dim': '#86cffa',
          'on-secondary-fixed': '#000000',
          'on-secondary-fixed-variant': '#00131e',
          'tertiary-fixed': '#9df3d9',
          'tertiary-fixed-dim': '#81d6bd',
          'on-tertiary-fixed': '#000000',
          'on-tertiary-fixed-variant': '#00150f',
          'surface-light': '#38393a',
        },
        dark: true,
        variables: {
          'overlay-background': '#1a1c1c',
        },
      },
    },
    // Enable CSS variables for dynamic theming
    variations: {
      colors: ['primary', 'secondary', 'accent', 'error', 'info', 'success', 'warning'],
      lighten: 2,
      darken: 2,
    },
  },

  // ===========================================================================
  // Icons — Material Symbols via @iconify/vue
  // Replaces @mdi/font (~550KB) with on-demand SVG icons (~0.3KB each)
  // All Vuetify internal icons are mapped via msAliases.
  // Usage: <v-icon icon="search" /> or prepend-inner-icon="account-circle"
  // ===========================================================================
  icons: {
    defaultSet: 'ms',
    aliases: msAliases,
    sets: { ms },
  },

  defaults,

  display: {
    mobileBreakpoint: 'sm',
    thresholds: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
      xxl: 2560,
    },
  },

  blueprint: md3,

  // ===========================================================================
  // Locale — UI Vuetify in italiano.
  // NB: Vuetify ha un sistema locale PROPRIO, indipendente da vue-i18n. Senza
  // questa config i componenti (incluso <v-date-picker>) restano in inglese a
  // prescindere dalla lingua del browser o di vue-i18n.
  // ===========================================================================
  locale: {
    locale: 'it',
    fallback: 'en',
    messages: { it: vuetifyIt },
  },

  // ===========================================================================
  // Date — adapter dayjs. `locale.it = 'it'` istanzia l'adapter con la locale
  // dayjs italiana quando la locale Vuetify attiva è 'it': mesi in italiano e
  // settimana che inizia di lunedì (weekStart della locale dayjs 'it').
  // ===========================================================================
  date: {
    adapter: DayJsAdapter,
    locale: {
      it: 'it',
    },
  },
})

export default vuetify

// =============================================================================
// Export Theme Utilities
// =============================================================================

export {
  gandolfiLightTheme,
  gandolfiDarkTheme,
  marketingLayerTheme,
  serviceLayerTheme,
  techLayerTheme,
}

export type ThemeName = 'light' | 'dark' | 'light-medium-contrast' | 'light-high-contrast' | 'dark-medium-contrast' | 'dark-high-contrast' | 'gandolfiLight' | 'gandolfiDark'
export type ThemeLayerName = 'marketingLayer' | 'serviceLayer' | 'techLayer'
export type AllThemeNames = ThemeName | ThemeLayerName
